import React, { createRef, useMemo } from 'react';
import {
  FormattedMessage,
  defineMessages,
  injectIntl,
  useIntl,
} from 'react-intl';
import cx from 'classnames';
import { Pagination, Dimmer, Loader } from 'semantic-ui-react';
import Slugger from 'github-slugger';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { renderLinkElement } from '@plone/volto-slate/editor/render';
import config from '@plone/volto/registry';
import withQuerystringResults from '@plone/volto/components/manage/Blocks/Listing/withQuerystringResults';
import { normalizeString } from '@plone/volto/helpers/Utils/Utils';

import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';

import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';

/**
 * SHADOW of @plone/volto ListingBody.
 *
 * Only change vs core: forward the `setSort` callback (injected by the shadowed
 * SearchBlockView from the search HOC's sort setters) down into the listing
 * template, so the tabular variation can drive per-column sort. Ports the
 * legacy archives' behaviour. Sibling `./withQuerystringResults` import is
 * rewritten to the absolute `@plone/volto/...` path (Volto customization rule).
 */
// BL-0157 (TAIUIT quality/empty-interactive): the prev/next items render only an
// svg, so the anchors had no accessible name. Semantic UI's Pagination forwards
// item props to the <a>; passing our own item object replaces its default
// 'Previous item' / 'Next item' labels, so they have to be supplied here.
const messages = defineMessages({
  previousPage: {
    id: 'Previous page',
    defaultMessage: 'Previous page',
  },
  nextPage: {
    id: 'Next page',
    defaultMessage: 'Next page',
  },
});

const Headline = ({ headlineTag, id, data = {}, listingItems, isEditMode }) => {
  let attr = { id };
  const slug = Slugger.slug(normalizeString(data.headline));
  attr.id = slug || id;
  const LinkedHeadline = useMemo(
    () => renderLinkElement(headlineTag),
    [headlineTag],
  );
  return (
    <LinkedHeadline
      mode={!isEditMode && 'view'}
      children={data.headline}
      attributes={attr}
      className={cx('headline', {
        emptyListing: !listingItems?.length > 0,
      })}
    />
  );
};

const ListingBody = withQuerystringResults((props) => {
  const {
    data = {},
    isEditMode,
    listingItems,
    totalPages,
    onPaginationChange,
    variation,
    currentPage,
    prevBatch,
    nextBatch,
    isFolderContentsListing,
    hasLoaded,
    id,
    content,
    setSort, // ** forwarded from the shadowed SearchBlockView
    searchMeta, // ** what the seam did — from the shadowed withQuerystringResults
    onSetSites, // ** set the site facet — for the zero-results escape hatches
    onSetSearchText, // ** rewrite the query — ditto (item 4's field prefixes)
  } = props;

  let ListingBodyTemplate;
  // Legacy support if template is present
  const variations = config.blocks?.blocksConfig['listing']?.variations || [];
  const defaultVariation = variations.filter((item) => item.isDefault)?.[0];

  if (data.template && !data.variation) {
    const legacyTemplateConfig = variations.find(
      (item) => item.id === data.template,
    );
    ListingBodyTemplate = legacyTemplateConfig.template;
  } else {
    ListingBodyTemplate =
      variation?.template ?? defaultVariation?.template ?? null;
  }

  const intl = useIntl();
  const listingRef = createRef();

  const NoResults = variation?.noResultsComponent
    ? variation.noResultsComponent
    : config.blocks?.blocksConfig['listing'].noResultsComponent;

  const HeadlineTag = data.headlineTag || 'h2';

  return (
    <>
      {data.headline && (
        <Headline
          headlineTag={HeadlineTag}
          id={id}
          listingItems={listingItems}
          data={data}
          isEditMode={isEditMode}
        />
      )}
      <SlotRenderer name="aboveListingItems" content={content} data={data} />
      {listingItems?.length > 0 ? (
        // item 30: once a result set exists the reducer keeps the stale rows
        // during a refetch (deliberate), which left facet/sort/page changes
        // with zero visual feedback. hasLoaded drops during the in-flight
        // window; the class dims the stale rows after a short delay so fast
        // responses never flicker, and aria-busy says the same to AT.
        <div
          ref={listingRef}
          className={hasLoaded ? 'pp-results' : 'pp-results is-busy'}
          aria-busy={!hasLoaded}
        >
          <ListingBodyTemplate
            items={listingItems}
            isEditMode={isEditMode}
            {...data}
            {...variation}
            setSort={setSort}
            searchMeta={searchMeta}
          />
          {totalPages > 1 && (
            <div className="pagination-wrapper">
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => {
                  !isEditMode &&
                    listingRef.current.scrollIntoView({ behavior: 'smooth' });
                  onPaginationChange(e, { activePage });
                }}
                firstItem={null}
                lastItem={null}
                prevItem={{
                  content: <Icon name={paginationLeftSVG} size="18px" />,
                  icon: true,
                  'aria-label': intl.formatMessage(messages.previousPage),
                  'aria-disabled': !prevBatch,
                  className: !prevBatch ? 'disabled' : null,
                }}
                nextItem={{
                  content: <Icon name={paginationRightSVG} size="18px" />,
                  icon: true,
                  'aria-label': intl.formatMessage(messages.nextPage),
                  'aria-disabled': !nextBatch,
                  className: !nextBatch ? 'disabled' : null,
                }}
              />
            </div>
          )}
        </div>
      ) : isEditMode ? (
        <div className="listing message" ref={listingRef}>
          {isFolderContentsListing && (
            <FormattedMessage
              id="No items found in this container."
              defaultMessage="No items found in this container."
            />
          )}
          {hasLoaded && NoResults && (
            <NoResults
              isEditMode={isEditMode}
              onSetSites={onSetSites}
              onSetSearchText={onSetSearchText}
              {...data}
            />
          )}
          <Dimmer active={!hasLoaded} inverted>
            <Loader indeterminate size="small">
              <FormattedMessage id="loading" defaultMessage="Loading" />
            </Loader>
          </Dimmer>
        </div>
      ) : (
        <div className="emptyListing">
          {hasLoaded && NoResults && (
            <NoResults
              isEditMode={isEditMode}
              onSetSites={onSetSites}
              onSetSearchText={onSetSearchText}
              {...data}
            />
          )}
          <Dimmer active={!hasLoaded} inverted>
            <Loader indeterminate size="small">
              <FormattedMessage id="loading" defaultMessage="Loading" />
            </Loader>
          </Dimmer>
        </div>
      )}
    </>
  );
});

export default injectIntl(ListingBody);
